import { Box, Grid } from '@mui/material';
import { useTheme } from '@mui/styles';
import { ClientWalletRolesMap } from '@tassat/common-definitions';
import { AssetTicker } from '@tassat/fred-extended-definitions';
import { Favorite as FavoriteIcon, Show as ShowIcon } from '@tassat/icon';
import { ClientRoleSymbol } from '@tassat/symbols';
import { ButtonType, WalletAddress, WalletTotalBalanceTile } from '@tassat/text';
import React from 'react';
import ReactGA from 'react-ga4';

import { obscureAccountNo } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';

import type { WalletDetailsProps } from '.';

export function WalletDetails(props: WalletDetailsProps) {
    const {
        classes,
        userAccountMapped,
        accountPositionStats,
        linkedBDAccountId
    } = props;

    const {
        userAccount,
        user,
        account
    } = userAccountMapped;

    const theme = useTheme<ApplicationMuiTheme>();

    const [ showWalletAddress, setShowWalletAddress ] = React.useState<boolean>(false);

    if (!userAccount || !account) return <div />;

    const favoriteWallet = async () => {
        try {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'view_account_toggle_favorite'
            });
            const response = await Worker.getInstance()
                .updateUserAccount({
                    UserAccountId: userAccount.UserAccountId,
                    ClientRoleId: userAccount.ClientRoleId,
                    IsFavorite: !userAccount.IsFavorite
                });
        } catch (error) {
            console.error(error);
        }
    };

    const sortedPositionStats = accountPositionStats ?
        [...accountPositionStats].sort((a, b) => {
            const aIsUSD = a.key === AssetTicker.USD;
            const bIsUSD = b.key === AssetTicker.USD;
            if (aIsUSD && !bIsUSD) return -1;
            if (!aIsUSD && bIsUSD) return 1;
            const keyCompare = a.key.localeCompare(b.key);
            if (keyCompare !== 0) return keyCompare;

            return (a.collateral ? 1 : 0) - (b.collateral ? 1 : 0);
        })
        : undefined;

    return (
        <Grid
            className={classes.infoContainer}
            container
            direction='row'
            item
            justifyContent='flex-start'
            sm={12}
            spacing={0}
        >
            <Grid
                className={classes.walletInfoTop}
                container
                direction='row'
                item
                justifyContent='flex-start'
                sm={12}
                spacing={0}
            >
                <Grid
                    className={classes.walletInfoTopLongNameContainer}
                    container
                    direction='column'
                    item
                    justifyContent='space-evenly'
                    sm={7}
                    spacing={0}
                    wrap='wrap'
                >
                    <Box className={classes.walletInfoTopLongName}>{account.AccountName}</Box>
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='flex-start'
                    sm={5}
                    spacing={0}
                >
                    <Grid
                        className={classes.walletInfoTopAccountContainer}
                        container
                        direction='row'
                        item
                        sm={12}
                        spacing={2}
                        wrap='nowrap'
                    >
                        <Grid
                            container
                            direction='column'
                            item
                            sm={9}
                            spacing={0}
                        >
                            <Box className={classes.walletInfoTopAccount}>
                                {obscureAccountNo((account as any).linkedBrokerDealerAccount)}
                            </Box>
                            <Grid
                                alignItems='center'
                                container
                                direction='row'
                                item
                                spacing={0}
                            >
                                <ClientRoleSymbol
                                    className={classes.statusIcon}
                                    role={ClientWalletRolesMap.find(role => role.id === userAccount.ClientRoleId).displayName}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            alignItems='flex-end'
                            className={classes.favoriteContainer}
                            container
                            direction='column'
                            item
                            spacing={0}
                            xs={3}
                        >
                            <FavoriteIcon
                                className={classes.favoriteIcon}
                                iconColor={userAccount.IsFavorite &&
                                    (theme?.app?.clientTheme.colors.highlightColor1 ||
                                        '#EDDA26')}
                                onClick={favoriteWallet}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                className={classes.walletInfoBottom}
                container
                direction='row'
                item
                justifyContent='space-between'
                sm={12}
                spacing={0}
            >
                <Grid
                    className={classes.walletInfoBottomBalanceContainer}
                    container
                    direction='column'
                    item
                    sm={6}
                    spacing={0}
                >
                    {/* <Box className={classes.walletInfoBottomBalanceNumber}>{formatAsCurrency(account.AvailableBalance)}</Box>
                    <Box className={classes.walletInfoBottomBalanceLabel}>Total Available Wallet Balance</Box> */}
                </Grid>
                {showWalletAddress ?
                    <Grid
                        alignItems='flex-end'
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={6}
                    >
                        <Grid
                            alignItems='center'
                            className={classes.showWalletAddressContainer}
                            container
                            direction='row'
                            onClick={(e) => setShowWalletAddress(false)}
                            spacing={0}
                            wrap='nowrap'
                        >
                            <ShowIcon
                                className={classes.showWalletAddressIcon}
                                iconColor={theme?.app?.clientTheme.colors.primaryColor ||
                                    '#375073'}
                            />
                            <Box className={classes.walletInfoBottomDetails}>Hide Account Details</Box>
                        </Grid>
                        <Box className={classes.walletAddressWarning}>ONLY USE THIS ADDRESS WITHIN THE {theme?.app?.clientTheme.names.applicationName.toUpperCase() || 'TASSATPAY'} PLATFORM.</Box>
                        <WalletAddress
                            buttonType={ButtonType.SMALL}
                            isAdmin={user.IsAdmin}
                            walletAddress={account.Account}
                        />
                    </Grid>
                    : (
                        <Grid
                            alignItems='center'
                            className={classes.showWalletAddressContainer}
                            container
                            direction='row'
                            item
                            onClick={(e) => {
                                ReactGA.event('click', {
                                    event_category: 'engagement',
                                    event_label: 'view_account_details'
                                });
                                setShowWalletAddress(true);
                            }}
                            spacing={0}
                            wrap='nowrap'
                            xs={6}
                        >
                            <ShowIcon
                                className={classes.showWalletAddressIcon}
                                iconColor={theme?.app?.clientTheme.colors.primaryColor ||
                                    '#375073'}
                            />
                            <Box className={classes.walletInfoBottomDetails}>View Account Details</Box>
                        </Grid>
                    )}
                <Grid
                    className={classes.walletTotalBalanceTile}
                    container
                    direction='column'
                    item
                    sm={12}
                    spacing={0}
                >
                    {sortedPositionStats && <WalletTotalBalanceTile stats={sortedPositionStats} />}
                </Grid>
            </Grid>
        </Grid>
    );
}